if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        if (activeMedia) this.prioritizeVariantMedia(activeMedia.dataset.mediaId);
      }

      prioritizeVariantMedia(mediaId) {
        const canonicalOrder = (this.dataset.mediaOrder || '').split(',').filter(Boolean);
        const anchors = [
          ...new Set([canonicalOrder[0], ...(this.dataset.variantMediaAnchors || '').split(',').filter(Boolean)]),
        ].filter((id) => canonicalOrder.includes(id));
        const rawMediaId = String(mediaId).substring(String(mediaId).lastIndexOf('-') + 1);
        const start = canonicalOrder.indexOf(rawMediaId);
        const anchorPositions = anchors.map((id) => canonicalOrder.indexOf(id)).sort((a, b) => a - b);

        if (anchors.length < 2 || start === -1 || !anchors.includes(rawMediaId)) return false;

        const end = anchorPositions.find((position) => position > start) ?? canonicalOrder.length;
        const activeGroup = canonicalOrder.slice(start, end);
        const prioritizedOrder = [...activeGroup, ...canonicalOrder.filter((id) => !activeGroup.includes(id))];

        const reorderList = (list, attribute) => {
          if (!list) return;
          const items = new Map(
            Array.from(list.querySelectorAll(`li[${attribute}]`)).map((item) => {
              const value = item.getAttribute(attribute);
              return [value.substring(value.lastIndexOf('-') + 1), item];
            })
          );
          const fragment = document.createDocumentFragment();

          prioritizedOrder.forEach((id) => {
            const item = items.get(id);
            if (item) fragment.appendChild(item);
          });
          items.forEach((item, id) => {
            if (!prioritizedOrder.includes(id)) fragment.appendChild(item);
          });
          list.appendChild(fragment);
        };

        reorderList(this.elements.viewer.querySelector('ul'), 'data-media-id');
        reorderList(this.elements.thumbnails?.querySelector('ul'), 'data-target');

        this.elements.thumbnails
          ?.querySelectorAll('[data-media-position]')
          .forEach((item, index) => item.setAttribute('data-media-position', index + 1));
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        return true;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend && !this.prioritizeVariantMedia(mediaId)) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
